import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import {
  CALL_INSIGHT_GLOBAL_FEEDBACK_FF,
  CallIntelAccordion,
  CIBannerPriorityGroupEnum,
  CIBannerPriorityList,
  CallIntelFeedbackBannerV2,
  CALL_INSIGHT_PANEL_V2_FF,
  generateTrackingId,
  getHighlightedCitation,
  TaskAccordion,
  UnsavedChangesModal,
  callStatusUtils,
  useCallIntelSubscription,
  canUseCallIntelPremiumFeatures,
} from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { CallQualityAccordionItem } from '@frontend/phone-panels';
import { useInitPriorityRendererGroup } from '@frontend/priority-renderer';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Accordion, BannerNotification, useContentOverflow, useModalControl } from '@frontend/design-system';
import { useCallInsightController } from '../../hooks/use-call-insight-controller';
import { useCallInsightPanelData } from '../../hooks/use-call-insight-panel-data';
import { useCallInsightPanelState } from '../../hooks/use-call-insight-panel-state';
import { useCallInsightPanelShallowStore } from '../../hooks/use-call-insight-panel-store';
import { useTaskHandlers } from '../../hooks/use-task-handlers';
import { CallInsightAccordionType, ScreenBaseProps, ScreenType } from '../../types';
import { shouldHideConversationForCallStatus } from '../../utils/call-insight-panel-util';
import { CallInsightContent } from '../call-insight-content';
import { CallInsightHeader } from '../call-insight-header';
import { NavigationButtons } from '../navigation-buttons';
import { CallBreakdownAccordion } from '../sections/call-breakdown-accordion';
import { CallDetailsSection } from '../sections/call-details-section';
import { CallInsightContactSection } from '../sections/call-insight-contact-section';
import { CallInsightTagsSection } from '../sections/call-insight-tags-section';
import { CallRecordingSection } from '../sections/call-recording-section';
import { ConversationAccordion } from '../sections/conversation-accordion';

export type CallInsightScreenProps = ScreenBaseProps<ScreenType.CALL_INSIGHTS>;

export const CallInsightScreen = ({
  panelStyles,
  source,
  showBackButton,
  onClose,
  setScreen,
}: CallInsightScreenProps) => {
  const { t } = useTranslation('call-insight-panel');

  const { modalProps, triggerProps } = useModalControl();

  const { isLoading, emitEvent, goToNextCall, goToPreviousCall } = useCallInsightController({
    source,
  });
  const { contexts, closeContext, updateCallInsight } = useCallInsightPanelShallowStore(
    'contexts',
    'closeContext',
    'updateCallInsight'
  );

  // Extract context and call info
  const panelContext = contexts[source];
  const callRecord = panelContext?.selectedCall as HydratedCallRecord;
  const hasNextCall = !!panelContext?.nextCallItem;
  const hasPreviousCall = !!panelContext?.previousCallItem;
  const callIntelInsights = callRecord?.callIntelInsights;
  const showCallIntelInsights = !!callIntelInsights;
  const locationIds = useMemo(
    () => (callRecord?.locationId ? ([callRecord.locationId] as string[]) : []),
    [callRecord?.locationId]
  );
  const {
    defaultAccordion,
    defaultCallBreakdownTab,
    isDemoAccount,
    highlightedTaskId: defaultHighlightedTaskId,
    defaultCallIntelTab,
    autoplayRecording,
    isPLGDemo,
  } = panelContext;

  const {
    callMetadata,
    callRecordingPath,
    hasAccessToCallRecording,
    hasAnyAccessToCallRecording,
    contactInfo,
    hasCallIntelAccess,
    isLoadingCallMetadata,
    isTaskFetching,
    isTasksEnabled,
    networkPerformance,
    taskQueryKey,
    refetchCallMetadata,
  } = useCallInsightPanelData({ callRecord, isDemoAccount, source });

  const { currentPlan } = useCallIntelSubscription({ locationIds });
  const hasCallIntelPremiumFeatures = canUseCallIntelPremiumFeatures(currentPlan);

  const { getFeatureFlagValue } = useAppFlagStore();
  const showGlobalFeedback = getFeatureFlagValue({
    locationIds,
    flagName: CALL_INSIGHT_GLOBAL_FEEDBACK_FF,
  });

  const isCallInsightV2Enabled = getFeatureFlagValue({
    locationIds,
    flagName: CALL_INSIGHT_PANEL_V2_FF,
  });

  const {
    activeAccordion,
    highlightedTaskId,
    unsavedChangesConfig,
    setActiveAccordion,
    setHighlightedTaskId,
    setUnsavedChangesConfig,
  } = useCallInsightPanelState({
    defaultAccordion,
    defaultHighlightedTaskId,
    showCallIntelInsights: showCallIntelInsights && hasCallIntelAccess,
    channelId: callRecord.channelId, // Re-init when channelId changes
  });

  // Check content overflow
  const [contentRef, isOverflowing] = useContentOverflow();

  // Initialize banner priority renderer group
  useInitPriorityRendererGroup({
    groupName: CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL,
    priorityList: CIBannerPriorityList[CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL] || [],
    resetPriorityMap: true,
    stabilizationTimeout: 1500, // Wait longer for all banner conditions to load
  });

  const { handleTasksUpdate, handleTaskDelete } = useTaskHandlers({
    queryKey: taskQueryKey,
    emitEvent,
    updateCallInsight,
  });

  const highlightedCitation = useMemo(
    () => getHighlightedCitation(callMetadata.tasks, highlightedTaskId),
    [callMetadata.tasks, highlightedTaskId]
  );

  const confirmBeforeAction = useCallback(
    (action: () => void) => {
      const { dontAskAgain, hasUnsavedChanges } = unsavedChangesConfig;
      if (!dontAskAgain && hasUnsavedChanges) {
        setUnsavedChangesConfig({ actionType: 'SHOW_MODAL', hasUnsavedChanges: true });
      } else {
        action();
      }
    },
    [setUnsavedChangesConfig, unsavedChangesConfig]
  );

  const handleAccordionChange = useCallback(
    (value: string | null) => {
      setActiveAccordion(value as CallInsightAccordionType | null);
      setHighlightedTaskId(null);
    },
    [setActiveAccordion, setHighlightedTaskId]
  );

  const handleUpdateCall = useCallback(
    (call: CallIntelTypes.CallRecord) => {
      updateCallInsight({ updatedCallIntelInsights: call as Call });
      emitEvent('callAnalysisUpdated', { call: call as Call });
    },
    [emitEvent, updateCallInsight]
  );

  const handleHighlightTask = useCallback(
    (taskId: string) => {
      setActiveAccordion('callIntel');
      setHighlightedTaskId(taskId);
    },
    [setActiveAccordion, setHighlightedTaskId]
  );

  const handleTagChange = useCallback(
    (
      action: 'added' | 'removed',
      {
        callRecord: updatedCallRecord,
        tagId,
        updatedTagIds,
      }: { callRecord: HydratedCallRecord; tagId: string; updatedTagIds: string[] }
    ) => {
      emitEvent(action === 'added' ? 'callTagAdded' : 'callTagRemoved', { callRecord: updatedCallRecord, tagId });
      emitEvent('callTagsUpdated', { callRecord: updatedCallRecord, tagIds: updatedTagIds });
    },
    [emitEvent]
  );

  const handleClose = useCallback(() => {
    const { hasUnsavedChanges, dontAskAgain } = unsavedChangesConfig;
    if (!dontAskAgain && hasUnsavedChanges) {
      setUnsavedChangesConfig({ hasUnsavedChanges: true, actionType: 'SHOW_MODAL' });
      return;
    }
    closeContext();
    !!onClose && onClose();
  }, [closeContext, onClose, setUnsavedChangesConfig, unsavedChangesConfig]);

  useEffect(() => {
    const { actionType, dontAskAgain, hasUnsavedChanges } = unsavedChangesConfig;
    if (!dontAskAgain && actionType === 'SHOW_MODAL' && hasUnsavedChanges) {
      triggerProps.onClick();
    }
  }, [triggerProps, unsavedChangesConfig]);

  const switchToCallPathMappingScreen = useCallback(
    (call: HydratedCallRecord) => {
      setScreen({
        screenType: ScreenType.CALL_PATH_MAPPING,
        data: { call },
      });
    },
    [setScreen]
  );

  const callFlags = callStatusUtils.getCallFlags(callIntelInsights);
  const callAnalysisFailed = callFlags.isFailedCall;
  const hideConversationForStatus = shouldHideConversationForCallStatus({
    status: callRecord?.status,
    aiOutcome: callRecord?.callIntelInsights?.aiOutcome,
  });
  const showConversationAccordion = !!callIntelInsights && !callAnalysisFailed && !hideConversationForStatus;

  const taskAccordion =
    callIntelInsights && !callFlags.isVoicemailCall && (isDemoAccount || hasCallIntelPremiumFeatures) ? (
      <Accordion.Item
        value='tasks'
        data-testid='tasks-accordion'
        trackingId={generateTrackingId({
          prefix: 'call-insight-panel',
          component: 'task-accordion',
          isDemoAccount,
        })}
      >
        <TaskAccordion.Header
          iconName={isPLGDemo ? 'premium-badge' : undefined}
          tasks={isTasksEnabled ? callMetadata.tasks : []}
        />
        <TaskAccordion.Body
          call={callIntelInsights}
          callParticipantsMetadata={callMetadata.callParticipantsMetadata}
          isLoading={isLoadingCallMetadata || isTaskFetching}
          tasks={callMetadata.tasks}
          isDemoAccount={isDemoAccount}
          onHighlightTask={handleHighlightTask}
          onUpdateTasks={handleTasksUpdate}
          onDeleteTask={handleTaskDelete}
          isTasksEnabled={isTasksEnabled}
        />
      </Accordion.Item>
    ) : null;

  const showCallIntelFeedbackOption =
    showCallIntelInsights &&
    hasCallIntelAccess &&
    !!callMetadata?.summary &&
    callFlags.isAnalyzed &&
    !callFlags.isPrivateCall;

  const showAIRFeedbackOption =
    !!(
      callRecord.callIntelInsights?.aiOutcome && callRecord.callIntelInsights.aiOutcome !== AIOutcome.AI_OUTCOME_UNKNOWN
    ) || !!callRecord.aiMetadata?.aiOutcome;

  return (
    <div css={[styles.wrapper, panelStyles]} data-testid='call-insight-screen'>
      <CallInsightHeader onClose={handleClose} showBackButton={showBackButton} />
      <CallInsightContent isLoading={isLoading} ref={contentRef}>
        <CallInsightContent.Lead>
          <CallInsightContactSection
            callDirection={callRecord?.direction ?? ''}
            contact={{
              ...contactInfo,
              defaultName: t('Unknown'),
            }}
            locationId={contactInfo.locationId}
          />
          {hasAnyAccessToCallRecording && (
            <CallRecordingSection
              call={callRecord}
              callRecordingPath={callRecordingPath}
              hasAccessToCallRecording={hasAccessToCallRecording}
              isDemoAccount={isDemoAccount}
              autoplayRecording={autoplayRecording}
            />
          )}
          <CallInsightTagsSection
            call={callRecord}
            source={source}
            onTagChange={handleTagChange}
            isDemoAccount={isDemoAccount}
          />
          <Divider css={styles.divider} />
        </CallInsightContent.Lead>
        <CallInsightContent.Body>
          {isCallInsightV2Enabled && !callFlags.isPrivateCall && callFlags.isFailedCall && (
            <BannerNotification
              status='error'
              message={t('Call Intelligence is unable to transcribe and analyze this call.')}
              css={{ width: 'fit-content' }}
            />
          )}
          <Accordion controlledValue={activeAccordion} css={styles.accordion} onChange={handleAccordionChange}>
            {isCallInsightV2Enabled ? (
              <>
                {showConversationAccordion && (
                  <ConversationAccordion
                    call={callIntelInsights}
                    callMetadata={callMetadata}
                    hasCallIntelAccess={hasCallIntelAccess}
                    isLoading={isLoadingCallMetadata || isTaskFetching}
                    isDemoAccount={isDemoAccount}
                    value='callIntel' // TODO: Change this when old call-intel accordion is completely removed
                  >
                    <ConversationAccordion.Header iconName={isPLGDemo ? 'premium-badge' : undefined} />
                    <ConversationAccordion.Body highlightedCitation={highlightedCitation} />
                  </ConversationAccordion>
                )}
                {!callAnalysisFailed && taskAccordion}
                <CallBreakdownAccordion
                  call={callIntelInsights}
                  callMetadata={callMetadata}
                  hydratedCall={callRecord}
                  callFlags={callFlags}
                  hasCallIntelAccess={hasCallIntelAccess}
                  isLoading={isLoadingCallMetadata || isTaskFetching}
                  isDemoAccount={isDemoAccount}
                  value='aboutThisCall'
                >
                  <CallBreakdownAccordion.Header
                    onViewCallPath={switchToCallPathMappingScreen}
                    iconName={isPLGDemo ? 'premium-badge' : undefined}
                  />
                  <CallBreakdownAccordion.Body
                    defaultSelectedTab={defaultCallBreakdownTab}
                    onUpdateCall={handleUpdateCall}
                    unsavedEditChangesConfig={unsavedChangesConfig}
                    setUnsavedEditChangesConfig={setUnsavedChangesConfig}
                    hasCallIntelPremiumFeatures={hasCallIntelPremiumFeatures}
                  />
                </CallBreakdownAccordion>
              </>
            ) : (
              <>
                {callIntelInsights && hasCallIntelAccess && !callFlags.isVoicemailCall && (
                  <Accordion.Item
                    value='callIntel'
                    data-testid='call-intelligence-accordion'
                    trackingId={generateTrackingId({
                      prefix: 'call-insight-panel',
                      component: 'call-intel-accordion',
                      isDemoAccount,
                    })}
                  >
                    <CallIntelAccordion.Header />
                    <CallIntelAccordion.Body
                      call={callIntelInsights}
                      isLoading={isLoadingCallMetadata || isTaskFetching}
                      isDemoAccount={isDemoAccount}
                      refetchCallMetadata={refetchCallMetadata}
                      callMetadata={callMetadata}
                      highlightedCitation={highlightedCitation}
                      onUpdateCall={handleUpdateCall}
                      unsavedEditChangesConfig={unsavedChangesConfig}
                      setUnsavedEditChangesConfig={setUnsavedChangesConfig}
                      defaultCallIntelTab={defaultCallIntelTab}
                      hideFeedback={showGlobalFeedback}
                    />
                  </Accordion.Item>
                )}

                {taskAccordion}

                <Accordion.Item
                  value='aboutThisCall'
                  data-testid='call-details-accordion'
                  trackingId={generateTrackingId({
                    prefix: 'CallInsights',
                    component: 'AboutThisCall',
                    isDemoAccount,
                  })}
                >
                  <CallDetailsSection call={callRecord} onClick={switchToCallPathMappingScreen} />
                </Accordion.Item>
              </>
            )}

            {networkPerformance.showQualityAccordion && (
              <Accordion.Item
                value='callQuality'
                data-testid='call-quality-accordion'
                trackingId={generateTrackingId({
                  prefix: 'call-insight-panel',
                  component: 'call-quality-accordion',
                  isDemoAccount,
                })}
              >
                <CallQualityAccordionItem
                  call={callRecord}
                  hasIssues={networkPerformance.hasPerformanceIssue}
                  onClick={() => {
                    setScreen({ screenType: ScreenType.CALL_QUALITY });
                  }}
                  hideFeedbackButton={showGlobalFeedback}
                />
              </Accordion.Item>
            )}
          </Accordion>
        </CallInsightContent.Body>
      </CallInsightContent>
      <CallInsightContent.Footer
        css={[styles.footer, isOverflowing && styles.borderTop]}
        data-testid='call-insight-footer'
      >
        {showGlobalFeedback && (
          <CallIntelFeedbackBannerV2
            call={callRecord}
            callMetadata={callMetadata}
            refetchCallMetadata={refetchCallMetadata}
            isDemoAccount={isDemoAccount}
            showCallIntelligenceOption={showCallIntelFeedbackOption}
            showNetworkPerformanceOption={networkPerformance.showQualityAccordion}
            showAIROption={showAIRFeedbackOption}
            networkPerformance={networkPerformance}
            containerStyles={{ flex: 1 }}
          />
        )}
        <NavigationButtons
          disableNext={!hasNextCall}
          disablePrevious={!hasPreviousCall}
          onNextClick={() => confirmBeforeAction(goToNextCall)}
          onPreviousClick={() => confirmBeforeAction(goToPreviousCall)}
          trackingIdBase='call-insight-panel'
        />
      </CallInsightContent.Footer>
      {unsavedChangesConfig.hasUnsavedChanges && unsavedChangesConfig.actionType === 'SHOW_MODAL' && (
        <UnsavedChangesModal
          modalProps={modalProps}
          unsavedEditChangesConfig={unsavedChangesConfig}
          setUnsavedEditChangesConfig={setUnsavedChangesConfig}
        />
      )}
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden auto;
  `,
  accordion: css`
    flex: 1 1 100%;

    > div {
      box-shadow: none;
      border: 1px solid ${theme.colors.neutral20};
      margin-bottom: ${theme.spacing(2)};
    }

    > div h3 button section div svg {
      fill: ${theme.colors.neutral70};
    }

    div[data-active='true'] h3 button {
      background-color: ${theme.colors.neutral5};
      border-bottom: 1px solid ${theme.colors.neutral5};

      section div svg {
        fill: ${theme.colors.neutral90};
      }
    }
  `,
  footer: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-direction: row;
    position: sticky;
    bottom: 0;
    background-color: ${theme.colors.white};
    padding: ${theme.spacing(3)};
    z-index: ${theme.zIndex.sticky};
  `,
  borderTop: css`
    border-top: 1px solid ${theme.colors.neutral20};
  `,
  divider: css`
    margin: ${theme.spacing(0)};
    border-color: ${theme.colors.neutral20};
  `,
};
